/**
 * contact-demo.js — 聯絡我們頁留言表單 DEMO 假成功（純前端，無後端）
 *
 * 背景：contact.html 的 <form action="/api/contact" method="post"> 尚無後端端點，原生送出
 *   會 POST 到不存在的路由（靜態主機回 404）。本控制器一律 preventDefault 中和該 404，
 *   並以 inline 成功回饋（既有 .alert--success）取代頁面跳轉。
 *
 * 行為對齊站上同類型「訊息表單」——clinic-detail consult（src/scripts/pages/clinic-detail.js）：
 *   preventDefault → 純前端驗證 → 送出中態（spinner）→ 模擬非同步 → 成功回饋 + form.reset()。
 *   差異：consult 成功開 Modal；本頁依 uiux 規格走「inline 成功」（淡出表單、原位顯成功卡片），
 *   不導頁、不開 modal、不另建一次性成功元件（重用 .alert--success）。
 *
 * 保留前端驗證（用戶決策）：只 bypass 後端，HTML5 驗證照常運作——
 *   form 無 novalidate，各必填欄帶原生 required（_form-field.html nativeRequired:true）。
 *   送出時逐欄 checkValidity()（涵蓋 required 空值 + type=email 格式），不合法 → 標
 *   .form-field--invalid + aria-invalid、聚焦首個無效欄、停（不顯成功）。
 *   註：contact.html 的欄位「無」data-field、「無」.form-field__error 元素（partial 未帶 error）—
 *      故不以 [data-field] 查欄、不注入逐欄文字訊息，僅紅框 + aria-invalid（原生 tooltip 提示），
 *      與 cr-form-card.js 的 querySelectorAll('.form-field__input') 迭代法同源。
 *
 * loading 態鏡像全站慣例（auth-forms.js / clinic-detail.js）：.is-submitting + 同一份
 *   SPINNER_SVG（.member-spinner）；spin keyframe 因前台 main.css 鏈不含 member-spin，
 *   由 pages.css 的 .page-contact scope 自備同等 contact-spin（reduced-motion 退「送出中…」純文字）。
 *
 * ════════════════════════════════════════════════════════════════════
 * TODO(backend) 2026-06-22：接後端時
 *   1) 移除下方 setTimeout DEMO 區塊（simulateSubmit）；
 *   2) submit 改 await fetch(form.action, { method:'POST', body: new FormData(form) })，
 *      成功（res.ok）才 revealSuccess() + form.reset()；失敗則 exitSubmitting() + 顯
 *      表單頂 .alert--danger（沿用 alert atom，比照 clinic-detail.js showError 模式）；
 *   3) 保留本檔「前端 checkValidity 為第一道」邏輯（離線/格式錯先擋，少打一次無效請求）；
 *   4) 視需要拿掉 contact.html 的成功容器「初始 hidden」改由回應驅動（現已是 JS 控）。
 * ════════════════════════════════════════════════════════════════════
 *
 * Listener cleanup：保留 teardown()，HMR dispose 時解綁，避免重複綁定。
 */

const SPINNER_SVG =
  '<svg class="member-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" ' +
  'stroke-dasharray="42" stroke-dashoffset="14" /></svg>';

// prefersReducedMotion 收進共用 util（原 inline 同式收斂，import 為 ESM hoisted）
import { prefersReducedMotion } from '../utils/dom.js';
// 欄位 invalid 標記收斂至 field-error.js（單一真相源）。contact 欄位無 .form-field__error /
// 無 .form-field__hint，故 setInvalid/clearInvalid 走 message-null 路徑：只切 aria-invalid +
// .form-field--invalid（helper 找不到 error 節點即不揭露、不碰 describedby），與原內聯等價。
import { setInvalid, clearInvalid } from '../components/field-error.js';

function init() {
  // root 守門早退：非 contact 頁（無 .contact-form）即不執行、不報錯。
  const form = document.querySelector('.contact-form');
  if (!form) return null;

  const successEl = document.querySelector('[data-contact-success]');
  const submitBtn = form.querySelector('button[type="submit"]');
  const submitLabel = submitBtn ? submitBtn.textContent.trim() : '送出訊息';

  /* ---------- 欄位 invalid 標記 → 收斂至 field-error.js（單一真相源）----------
     contact 欄位無 .form-field__error 元素，故 setInvalid/clearInvalid 走 message-null 路徑：
     只切 .form-field--invalid + aria-invalid，不注入逐欄文字訊息（原生 required/email 由
     瀏覽器 tooltip 提示）。helper 找不到 error 節點即不揭露、不碰 hint / describedby，等價舊內聯。 */
  function clearFieldErrors() {
    form.querySelectorAll('.form-field__input').forEach((input) => clearInvalid(input));
  }

  /** 逐欄 HTML5 checkValidity（含 required 空值 + type=email 格式）。回傳首個無效欄或 null。 */
  function validateAll() {
    let firstInvalid = null;
    form.querySelectorAll('.form-field__input').forEach((input) => {
      if (input.disabled) return;
      clearInvalid(input);
      if (!input.checkValidity()) {
        setInvalid(input, null); // message-null：僅紅框 + aria-invalid（無 __error 節點，沿用原生 tooltip）
        if (!firstInvalid) firstInvalid = input;
      }
    });
    return firstInvalid;
  }

  /* ---------- 送出中態（鏡像 auth-forms.js / clinic-detail.js）---------- */
  function enterSubmitting() {
    form.classList.add('is-submitting');
    if (!submitBtn) return;
    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-disabled', 'true');
    submitBtn.setAttribute('aria-busy', 'true');
    submitBtn.innerHTML = prefersReducedMotion()
      ? '送出中…'
      : SPINNER_SVG + '<span>送出中…</span>';
  }
  // eslint-disable-next-line no-unused-vars -- Reserved for the TODO(backend) fetch-failure path that restores the submitting UI.
  function exitSubmitting() {
    form.classList.remove('is-submitting');
    if (!submitBtn) return;
    submitBtn.disabled = false;
    submitBtn.removeAttribute('aria-disabled');
    submitBtn.removeAttribute('aria-busy');
    submitBtn.textContent = submitLabel;
  }

  /* ---------- inline 成功：淡出表單、原位顯 .alert--success、移焦讓 AT 朗讀 ---------- */
  function revealSuccess() {
    form.hidden = true;
    if (!successEl) return;
    successEl.hidden = false;
    // 進場 class 觸發 opacity 0→1 + translateY（CSS .page-contact scope，reduced-motion 即時）。
    // rAF 等版面就緒再加 class，確保 transition 從初始態起跑（非 hidden 解除瞬間跳到末態）。
    requestAnimationFrame(() => {
      successEl.classList.add('is-revealed');
      // 焦點移到成功容器（tabindex=-1），AT 隨 aria-live=polite 朗讀（鏡像 auth-forms.js initSuccessArrival）。
      if (typeof successEl.focus === 'function') successEl.focus();
    });
  }

  /* demo 模擬非同步送出（無後端）：接後端時換成 fetch(...)，見檔頭 TODO(backend)。 */
  // TODO(backend): 移除 simulateSubmit DEMO，submit 改 await fetch(form.action,{method:POST,body:new FormData(form)})，res.ok 才 revealSuccess — 見 docs/BACKEND_HANDOFF.md#contact-form
  function simulateSubmit() {
    return new Promise((resolve) => {
      window.setTimeout(resolve, 800);
    });
  }

  function onSubmit(e) {
    // 一律攔截：原生 POST 到 /api/contact 永不觸發（這一步即中和 404）。
    e.preventDefault();

    // 重入守備：送出中再點（disabled 已擋點擊，這裡再保險擋 Enter 連送）。
    if (form.classList.contains('is-submitting')) return;

    // 保留前端驗證：不合法 → 標紅 + 聚焦首個無效欄，停（不顯成功）。
    const firstInvalid = validateAll();
    if (firstInvalid) {
      // 7.3 修復：用 reportValidity() 取代 focus()——除聚焦外，同時觸發瀏覽器原生驗證提示氣泡
      //（例：「請填寫這個欄位」/「請在電子郵件地址中加入 @」），讓使用者看到「哪裡錯、怎麼改」，
      // 而非僅紅框（對非螢幕報讀使用者無聲）。reportValidity 本身已 focus 首個無效控制項，故不再另呼 focus。
      firstInvalid.reportValidity();
      return;
    }

    enterSubmitting();
    simulateSubmit().then(() => {
      revealSuccess();
      form.reset();
    });
  }

  // input/change 即時清除該欄 error（改善體驗，對齊 clinic-detail.js）。
  function onFieldInput(e) {
    const input = e.target.closest('.form-field__input');
    if (!input) return;
    // 僅在該欄目前為 invalid 時才清（避免無謂屬性更動）；清除走 field-error.js clearInvalid。
    if (input.closest('.form-field')?.classList.contains('form-field--invalid')) {
      clearInvalid(input);
    }
  }

  // 「清除」鈕（type=reset）按下後同步清掉殘留 invalid 視覺（reset 不會自動移除 class）。
  function onReset() {
    clearFieldErrors();
  }

  form.addEventListener('submit', onSubmit);
  form.addEventListener('input', onFieldInput);
  form.addEventListener('change', onFieldInput);
  form.addEventListener('reset', onReset);

  return function teardown() {
    form.removeEventListener('submit', onSubmit);
    form.removeEventListener('input', onFieldInput);
    form.removeEventListener('change', onFieldInput);
    form.removeEventListener('reset', onReset);
  };
}

let teardown = null;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    teardown = init();
  });
} else {
  teardown = init();
}

// Vite HMR：模組更新前解綁，避免 listener 重複疊加。
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (typeof teardown === 'function') teardown();
  });
}
